import { useEffect, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import styled from 'styled-components';
import { FavoriteItemGroupDTO, ImageReportType } from '../../../api/api';
import { ChevronLeft } from '../../../assets/icons/ChevronLeft';
import { ChevronRight } from '../../../assets/icons/ChevronRight';
import { ImageErrorIcon } from '../../../assets/icons/ImageErrorIcon';
import Button from '../../../components/button/Button';
import IconButton from '../../../components/icon-button/IconButton';
import Select, { MenuItem } from '../../../components/select/Select';
import Typography from '../../../components/typography/Typography';
import { GoodsData } from '../../../models/Goods';
import ItemService from '../../../services/ItemService';
import NotificationService from '../../../services/NotificationService';
import n1LightTheme from '../../../theme/light-theme';
import { getEnumDisplayValue } from '../../../utils/enumUtils';
import { log } from '../../../utils/logging/log';
import ProductImageService from '../../../services/ProductImageService';
import DialogView from '../../../components/dialog-view/DialogView';
import LoadingOverlay from '../../../components/loading-overlay/LoadingOverlay';
import NavigationHeader from '../components/NavigationHeader';
import { isDevice } from '../../../utils/device-handling/deviceDetectionUtils';
import Groups from './Groups';

import { AddToListIcon } from '../../../assets/icons/AddToListIcon';

interface Props {
  good?: GoodsData;
  handleSelectGoods?: (goodsId?: string, goodsName?: string, amount?: number) => void;
  handleClose: () => void;
  groups?: FavoriteItemGroupDTO[];
  setGroupsCallback?: (groups: FavoriteItemGroupDTO[]) => void;
}

const ImageViewer = (props: Props) => {
  const { good, handleSelectGoods, handleClose, groups, setGroupsCallback } = props;
  const sharepointImages = good?.pictureLinks;
  const [currentImage, setCurrentImage] = useState(1);
  const [images, setImages] = useState<string[]>([]);
  const [showErrorImage, setShowErrorImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<ImageReportType | ''>('');
  const fullscreen = isDevice();
  const [showGroups, setShowGroups] = useState(false);

  useEffect(() => {
    if (!sharepointImages || sharepointImages.length === 0) return;
    setLoading(true);
    Promise.all(sharepointImages.map((image) => ProductImageService.getProductImage(image)))
      .then((responses) => {
        const result = responses.map((response) => URL.createObjectURL(response.data));
        setImages(result);
      })
      .catch(() => {
        setShowErrorImage(true);
        setSelectedFeedback(ImageReportType.NotFound);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sharepointImages]);

  useEffect(() => {
    if (!images || !selectedFeedback) return;

    setLoading(true);
    ItemService.reportImage({
      imageUrl: images[currentImage - 1],
      itemId: good?.goodsId,
      reportType: selectedFeedback
    })
      .then(() => {
        if (selectedFeedback === ImageReportType.NotFound) {
          NotificationService.warning('Billede kunne ikke findes. Fejlen er rapporteret.');
        } else {
          NotificationService.success(`Billedet er blevet rapporteret som "${getEnumDisplayValue(selectedFeedback)}".`);
        }
      })
      .catch((error) => {
        log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentImage, images, selectedFeedback, good?.goodsId]);

  return (
    <StyledDialogView fullscreen={fullscreen} handleClose={handleClose}>
      {loading ? (
        <LoadingOverlay />
      ) : (
        <>
          {good?.goodsId && showGroups && groups && setGroupsCallback && (
            <Groups
              handleClose={() => setShowGroups(false)}
              groups={groups}
              itemId={good.goodsId}
              setGroupsCallback={setGroupsCallback}
            />
          )}
          <NavigationHeader headerTitle={good?.goodsName ?? good?.goodsId ?? '-'} onClose={handleClose} />
          <Container>
            <Header>
              <FeedbackButtonContainer>
                <StyledSelect
                  disabled={loading || !!selectedFeedback}
                  label="Feedback"
                  onChange={(e) => setSelectedFeedback(e.target.value as ImageReportType)}
                  value={selectedFeedback}
                  IconComponent={() => null}
                >
                  <MenuItem value={ImageReportType.WrongItem}>{getEnumDisplayValue(ImageReportType.WrongItem)}</MenuItem>
                  <MenuItem value={ImageReportType.BadQuality}>{getEnumDisplayValue(ImageReportType.BadQuality)}</MenuItem>
                  <MenuItem sx={{ display: 'none' }} value={ImageReportType.NotFound}>
                    {getEnumDisplayValue(ImageReportType.NotFound)}
                  </MenuItem>
                </StyledSelect>
              </FeedbackButtonContainer>
            </Header>
            <ImageContainer>
              {showErrorImage ? (
                <ErrorImage>
                  <ImageErrorIcon color={n1LightTheme.palette.grey.black60} size="100px" />
                  <Typography margin="30px">Billede ikke tilgængeligt</Typography>
                </ErrorImage>
              ) : (
                <TransformWrapper centerOnInit maxScale={50}>
                  <TransformComponent
                    wrapperStyle={{ width: '100%', height: '100%' }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <Image src={images[currentImage - 1]} alt="Billede ikke tilgængeligt" />
                  </TransformComponent>
                </TransformWrapper>
              )}
            </ImageContainer>
            <ButtonBar>
              <StyledButton
                disabled={currentImage === 1}
                onClick={() => setCurrentImage((prev) => prev - 1)}
                variant="secondary"
              >
                <ChevronLeft size="12px" />
                Forrige
              </StyledButton>
              <Typography variant="p">{`${currentImage} / ${images?.length ?? 0}`}</Typography>
              <StyledButton
                disabled={currentImage === images?.length || showErrorImage}
                onClick={() => setCurrentImage((prev) => prev + 1)}
                variant="secondary"
              >
                Næste
                <ChevronRight size="12px" />
              </StyledButton>
            </ButtonBar>
            <DetailsContainer>
              <Detail>
                <Typography color="darkGrey" variant="h6">
                  Varenummer
                </Typography>
                <Typography variant="h6">{good?.goodsId}</Typography>
              </Detail>
              <Detail>
                <Typography color="darkGrey" variant="h6">
                  Søgt navn
                </Typography>
                <Typography variant="h6">{good?.searchName ?? '-'}</Typography>
              </Detail>
              <Detail>
                <Typography color="darkGrey" variant="h6">
                  Varegruppe
                </Typography>
                <Typography variant="h6">{good?.goodsGroupId ?? '-'}</Typography>
              </Detail>
              <Row>
                {good?.goodsId && groups && groups.length > 0 && (
                  <StyledIconButton variant="outlined" onClick={() => setShowGroups(true)}>
                    <AddToListIcon strokeWidth="0" size="24px" />
                  </StyledIconButton>
                )}
                {handleSelectGoods && (
                  <Button
                    onClick={() => {
                      handleSelectGoods(good?.goodsId, good?.goodsName, 1);
                      handleClose();
                    }}
                  >
                    Læg i kurv
                  </Button>
                )}
              </Row>
            </DetailsContainer>
          </Container>
        </>
      )}
    </StyledDialogView>
  );
};

const StyledDialogView = styled(DialogView)<{ fullscreen: boolean }>`
  display: block !important;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 50px;
  width: 80vw;
  height: 80vh;
  max-height: 620px;
  max-width: 1000px;
`;

const ButtonBar = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: ${(props) => props.theme.palette.grey.black5};
  width: 100%;
  align-items: center;
`;

const Image = styled.img`
  object-fit: cover;
  height: 100%;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  max-height: 400px;
  width: 100%;
`;

const StyledButton = styled(Button)`
  background-color: transparent;
  border: 0;
  font-size: 14px;
`;

const Detail = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0px;
  width: 100%;
`;

const ErrorImage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #dbdbdb;
  width: 100%;
  height: 100%;
  color: ${(props) => props.theme.palette.grey.black60};
`;

const FeedbackButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin: 20px 0px;
`;

const Header = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  height: 80px;
`;

const StyledIconButton = styled(IconButton)`
  width: 50px;
  height: 50px;
  margin: 0px 10px;
`;

const StyledSelect = styled(Select)`
  width: 200px;
`;

const Row = styled.div`
  display: flex;
`;

export default ImageViewer;
